import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Form, Radio, Checkbox, TextArea, Input, Header, Segment, Divider, Icon, Message, Popup } from "semantic-ui-react";
import {
  functionGetStrategyFeedbackState,
  functionSubmitStrategyFeedback,
} from "../../../../app/firestore/functionsService";

// Reusable Strategy feedback panel (spec sections 20-28). Pricing supplies
// pricingFeedbackConfig; future strategy types (Negotiation, Marketing,
// Offer, Inspection) provide their own config with the same shape and reuse
// this same component/submission/reward logic - layout, cap enforcement,
// and Firestore storage are never re-implemented per strategy type.
export default function StrategyFeedback({
  transactionId,
  propertyKey,
  propertyAddress,
  strategyType,
  generationId,
  generatedRecommendationSnapshot,
  engineMetadata,
  config,
  // Optional controlled-expansion mode: when a parent wants to host its own
  // "Give Feedback" trigger button elsewhere (e.g. in a bottom action row)
  // instead of the toggle button below, it passes `expanded`/`onToggleExpanded`
  // (to drive open/close from outside) and `hideToggle` (to suppress this
  // component's own collapsed-state toggle button). `onStatusChange` reports
  // the current button label/completion state back up so the parent's own
  // button can reflect "Feedback Submitted ✓" etc. Uncontrolled usage
  // (StrategyResultViewerModal) omits all of these and behaves as before.
  expanded: controlledExpanded,
  onToggleExpanded,
  onStatusChange,
  hideToggle,
}) {
  const [feedbackState, setFeedbackState] = useState(null);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const setExpanded = onToggleExpanded || setInternalExpanded;
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [creditAwarded, setCreditAwarded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (transactionId && propertyKey && strategyType) {
      functionGetStrategyFeedbackState({ transactionId, propertyKey, strategyType })
        .then((data) => {
          if (!cancelled) setFeedbackState(data);
        })
        .catch(() => {
          if (!cancelled) setFeedbackState(null);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [transactionId, propertyKey, strategyType]);

  function setAnswer(id, value) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }

  function toggleCheckboxAnswer(id, option) {
    setAnswers((prev) => {
      const current = prev[id] || [];
      const next = current.includes(option) ? current.filter((o) => o !== option) : [...current, option];
      return { ...prev, [id]: next };
    });
  }

  function isComplete() {
    return config.questions.every((q) => !q.required || Boolean(answers[q.id]));
  }

  async function handleSubmit() {
    if (!isComplete()) {
      toast.error("Please answer the required questions before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await functionSubmitStrategyFeedback({
        transactionId,
        propertyKey,
        propertyAddress,
        strategyType,
        generationId,
        generatedRecommendationSnapshot: generatedRecommendationSnapshot || null,
        engineMetadata: engineMetadata || null,
        structuredFeedback: answers,
      });
      setSubmitted(true);
      setCreditAwarded(!!result.creditAwarded);
      if (result.creditAwarded) {
        toast.success("Thanks for your feedback! You've earned 1 Strategy Credit.");
      } else {
        toast.success("Thanks for your feedback!");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to submit feedback right now.");
    } finally {
      setSubmitting(false);
    }
  }

  function renderQuestion(question) {
    const showFollowUp = question.followUp && question.followUp.showWhen(answers);
    return (
      <div key={question.id} className="medium bottom margin">
        <p className="bold zero bottom margin">
          {question.prompt}
          {question.required && <span className="text red"> *</span>}
        </p>
        {question.type === "radio" && (
          <Form.Group grouped className="small top margin">
            {question.options.map((option) => (
              <Form.Field key={option}>
                <Radio
                  label={option}
                  name={question.id}
                  value={option}
                  checked={answers[question.id] === option}
                  onChange={() => setAnswer(question.id, option)}
                />
              </Form.Field>
            ))}
          </Form.Group>
        )}
        {question.type === "checkboxes" && (
          <Form.Group grouped className="small top margin">
            {question.options.map((option) => (
              <Form.Field key={option}>
                <Checkbox
                  label={option}
                  checked={(answers[question.id] || []).includes(option)}
                  onChange={() => toggleCheckboxAnswer(question.id, option)}
                />
              </Form.Field>
            ))}
          </Form.Group>
        )}
        {question.type === "text" && (
          <TextArea
            className="small top margin"
            style={{ width: "100%" }}
            rows={2}
            value={answers[question.id] || ""}
            onChange={(e) => setAnswer(question.id, e.target.value)}
          />
        )}
        {question.type === "priceRange" && (
          <div className="small top margin">
            {question.fields.map((field) => (
              <div key={field.id} className="small bottom margin">
                <label>{field.label}</label>
                <Input
                  fluid
                  icon="dollar"
                  iconPosition="left"
                  type="number"
                  value={answers[field.id] || ""}
                  onChange={(e) => setAnswer(field.id, e.target.value)}
                />
              </div>
            ))}
          </div>
        )}
        {showFollowUp && (
          <div className="small top margin" style={{ paddingLeft: "20px" }}>
            <p className="small bottom margin text-grey">{question.followUp.prompt}</p>
            <TextArea
              style={{ width: "100%" }}
              rows={2}
              value={answers[question.followUp.id] || ""}
              onChange={(e) => setAnswer(question.followUp.id, e.target.value)}
            />
          </div>
        )}
      </div>
    );
  }

  let buttonLabel = "Give Feedback + Earn 1 Credit";
  if (feedbackState?.alreadyRewarded || submitted) {
    buttonLabel = submitted && creditAwarded ? "Feedback Submitted ✓ (+1 Credit)" : "Feedback Submitted ✓";
  } else if (feedbackState?.capReached) {
    buttonLabel = "Give Feedback";
  }

  const showCapMessage = !feedbackState?.alreadyRewarded && !submitted && feedbackState?.capReached;
  const alreadyDone = feedbackState?.alreadyRewarded || submitted;
  // A cap of exactly 0 (whether the global Maximum Feedback Credits Per User
  // setting or a per-user override - see feedback.js/admin.js) means this
  // user was never eligible to earn a credit here at all, as opposed to
  // having already reached a normal positive cap - so the button doesn't
  // show rather than reading "capped"/disabled.
  const hidden = feedbackState?.maxFeedbackCreditsPerUser === 0 && !alreadyDone;

  useEffect(() => {
    if (onStatusChange) {
      onStatusChange({ buttonLabel, alreadyDone, hidden });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonLabel, alreadyDone, hidden]);

  if (hidden) return null;

  if (!expanded) {
    if (hideToggle) return null;
    return (
      <Popup
        disabled={!alreadyDone}
        content="Feedback is already complete for this Strategy on this property."
        trigger={
          <span>
            <Button
              basic
              icon
              labelPosition="left"
              disabled={alreadyDone}
              onClick={() => !alreadyDone && setExpanded(true)}
              data-test="strategy-feedback-toggle"
            >
              <Icon name="comment outline" />
              {buttonLabel}
            </Button>
          </span>
        }
      />
    );
  }

  return (
    <Segment style={{ backgroundColor: "#eef6fc", borderColor: "#c9e3f5" }}>
      <Header as="h4" color="blue" className="zero bottom margin">
        Give Feedback
      </Header>
      <p className="small top margin text-grey">
        Takes about 30-60 seconds. Your feedback helps improve this analysis feature. Receive 1 free credit for submitting good feedback.
      </p>
      {showCapMessage && (
        <Message size="small" info>
          You've earned the maximum Strategy Credits available for feedback - your feedback is still valuable
          and welcome.
        </Message>
      )}
      <Divider />
      {alreadyDone ? (
        <Message positive size="small">
          <Icon name="check circle" /> Feedback already submitted for this Strategy on this property.
        </Message>
      ) : (
        <>
          <Form>{config.questions.map(renderQuestion)}</Form>
          <Divider />
          <Button primary loading={submitting} disabled={submitting} onClick={handleSubmit}>
            Submit Feedback
          </Button>
          <Button basic disabled={submitting} onClick={() => setExpanded(false)}>
            Cancel
          </Button>
        </>
      )}
    </Segment>
  );
}
